import { useMutation, useQueryClient } from "@tanstack/react-query";
import { config } from "@/lib/config";
import type { MyAccessRequest } from "../queries/fetchMyAccessRequests";

interface CreateAccessRequestInput {
    tenantId: string;
    message?: string;
}

interface CreateAccessRequestResponse {
    success: boolean;
    accessRequest: MyAccessRequest;
}

// Custom error class for auth-related errors
class AuthTokenExpiredError extends Error {
    constructor(message: string = "Your session has expired. Please log in again.") {
        super(message);
        this.name = "AuthTokenExpiredError";
    }
}

async function createAccessRequest(
    temporaryToken: string,
    input: CreateAccessRequestInput,
): Promise<MyAccessRequest> {
    const response = await fetch(`${config.apiUrl}/api/v1/my/access-requests`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${temporaryToken}`,
        },
        body: JSON.stringify(input),
    });

    // Handle token expiration specifically
    if (response.status === 401) {
        // Dispatch event to notify auth context of session expiry
        window.dispatchEvent(new CustomEvent("auth:session_expired"));
        throw new AuthTokenExpiredError();
    }

    const data: CreateAccessRequestResponse = await response.json();

    if (!response.ok) {
        throw new Error((data as unknown as { error: string }).error || "Failed to create access request");
    }

    return data.accessRequest;
}

export function useCreateMyAccessRequestMutation(temporaryToken: string | null) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: CreateAccessRequestInput) => {
            if (!temporaryToken) {
                throw new Error("No temporary token available");
            }
            return createAccessRequest(temporaryToken, input);
        },
        onSuccess: () => {
            // Invalidate queries to refetch data
            queryClient.invalidateQueries({ queryKey: ["myAccessRequests"] });
            queryClient.invalidateQueries({ queryKey: ["availableTenants"] });
        },
    });
}
